/* eslint-disable react/no-danger */
import React, { HTMLAttributes, PropsWithChildren, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import SubPageTitle from 'components/_common/panel/SubPageTitle';
import { useProgramPanel } from 'contexts/programPanelContext';
import { Box, BoxSection } from 'components/_new/Box';
import { Table } from 'components/_new/Table/Table';
import { useQuery } from '@tanstack/react-query';
import { CommunicationMentibotInfoResult, programsApi } from 'api';
import { Badge } from 'components/_new/Badge';
import { Dialog, DialogBaseExtendProps } from 'components/_new/Dialog';
import { useAuth } from 'contexts/authContext';
import { contentParser } from 'utils/contentParser';
import { SelectButtonGroup } from 'components/_new/SelectButtonGroup';
import { useScope } from 'contexts/scopeContext';
import { MessageBox } from 'components/_new/MessageBox';
import { Link } from 'components/_new/Link';
import { Button } from '../_new/Button';

const prepareTimeString = (t: any, hours: number) => {
	const absHours = Math.abs(hours);
	if (hours < 0) {
		if (absHours < 24) {
			return t('programPanel.post.mentibot.timeDescription.hoursBeforeSession', { count: absHours });
		}
		if (absHours >= 24) {
			const days = Math.round(absHours / 24);
			return t('programPanel.post.mentibot.timeDescription.daysBeforeSession', { count: days });
		}
	}
	if (hours > 0) {
		if (absHours < 24) {
			return t('programPanel.post.mentibot.timeDescription.hoursAfterSession', { count: absHours });
		}
		if (absHours >= 24 * 7) {
			const weeks = Math.round(absHours / 24 / 7);
			if (weeks === 1) {
				return t('programPanel.post.mentibot.timeDescription.weekAfterSession');
			}
			return t('programPanel.post.mentibot.timeDescription.weeksAfterSession', { count: weeks });
		}
		if (absHours >= 24) {
			const days = Math.round(absHours / 24);
			return t('programPanel.post.mentibot.timeDescription.daysAfterSession', { count: days });
		}
	}
	return null;
};

type IFrameProps = PropsWithChildren<
	HTMLAttributes<HTMLIFrameElement> & {
		title: string;
	}
>;

const IFrame = ({ title, children, ...props }: IFrameProps) => {
	const [ref, setRef] = useState<HTMLIFrameElement | null>(null);
	const container = ref?.contentWindow?.document?.body;

	return (
		<iframe ref={(el) => setRef(el)} title={title} {...props}>
			{container && createPortal(children, container)}
		</iframe>
	);
};

type PreviewTemplateDialogProps = DialogBaseExtendProps & {
	mailTemplateContent?: string | null;
	postTemplateContent?: string | null;
};

const PreviewTemplateDialog = ({
	mailTemplateContent,
	postTemplateContent,
	...dialogProps
}: PreviewTemplateDialogProps) => {
	const { t } = useTranslation();
	const parser = contentParser({});

	const defaultMode = postTemplateContent ? 'post' : 'mail';
	const [mode, setMode] = useState<'mail' | 'post' | null>(dialogProps.visible ? defaultMode : null);

	useEffect(() => {
		return () => {
			setMode(defaultMode);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dialogProps.visible]);

	return (
		<Dialog
			{...dialogProps}
			title={t('programPanel.post.mentibot.preview.title')}
			size="md"
			headerRight={
				mailTemplateContent && postTemplateContent ? (
					<SelectButtonGroup
						defaultValue={defaultMode}
						value={mode}
						options={[
							{
								key: 'post',
								label: t('programPanel.post.mentibot.preview.postMode'),
								value: 'post',
								variant: 'primary',
							},
							{
								key: 'mail',
								label: t('programPanel.post.mentibot.preview.mailMode'),
								value: 'mail',
								variant: 'primary',
							},
						]}
						onChange={({ value }) => {
							setMode(value);
						}}
					/>
				) : undefined
			}
		>
			{mode === 'mail' && mailTemplateContent && (
				<IFrame
					title={t('programPanel.post.mentibot.preview.title')}
					className="w-full border-none"
					style={{ minHeight: 500 }}
				>
					{parser.parse(mailTemplateContent)}
				</IFrame>
			)}
			{mode === 'post' && postTemplateContent && (
				<div dangerouslySetInnerHTML={{ __html: postTemplateContent || '' }} style={{ minHeight: 500 }} />
			)}
		</Dialog>
	);
};

const SessionNotificationInfoTable = (props: {
	sessionNotifications: CommunicationMentibotInfoResult['sessionNotifications'];
	isFetching: boolean;
}) => {
	const { sessionNotifications, isFetching } = props;
	const { t } = useTranslation();

	const [selectedPreviewKey, setSelectedPreviewKey] = useState<null | string>(null);
	const previewTemplateMailContent =
		sessionNotifications.find(({ key }: any) => key === selectedPreviewKey)?.template || null;

	return (
		<>
			<PreviewTemplateDialog
				visible={Boolean(selectedPreviewKey) && Boolean(previewTemplateMailContent)}
				onHide={() => {
					setSelectedPreviewKey(null);
				}}
				mailTemplateContent={
					typeof previewTemplateMailContent === 'object'
						? previewTemplateMailContent?.mail
						: previewTemplateMailContent
				}
				postTemplateContent={
					typeof previewTemplateMailContent === 'object'
						? previewTemplateMailContent?.post
						: previewTemplateMailContent
				}
			/>
			<Table
				keyOptionProp="key"
				loading={isFetching}
				data={sessionNotifications}
				columns={[
					{
						name: 'sessionNumber',
						label: t('programPanel.post.mentibot.table.sessionNumber'),
						body: (item) => (
							<div className="flex flex-row gap-2">
								<span>{item.sessionNumber}</span>
								{item.sessionNumberName === 'first' ? (
									<Badge>{t('programPanel.post.mentibot.table.sessionTypes.first')}</Badge>
								) : null}
								{item.sessionNumberName === 'second' ? (
									<Badge>{t('programPanel.post.mentibot.table.sessionTypes.second')}</Badge>
								) : null}
								{item.sessionNumberName === 'middle' ? (
									<Badge>{t('programPanel.post.mentibot.table.sessionTypes.middle')}</Badge>
								) : null}
								{item.sessionNumberName === 'penultimate' ? (
									<Badge>{t('programPanel.post.mentibot.table.sessionTypes.penultimate')}</Badge>
								) : null}
								{item.sessionNumberName === 'last' ? (
									<Badge>{t('programPanel.post.mentibot.table.sessionTypes.last')}</Badge>
								) : null}
							</div>
						),
						min: true,
					},
					{
						name: 'title',
						label: t('programPanel.post.mentibot.table.title'),
						onCellClick: ({ key }) => {
							setSelectedPreviewKey(key);
						},
					},
					{
						name: 'time',
						label: t('programPanel.post.mentibot.table.time'),
						body: ({ hours }) => prepareTimeString(t, hours),
					},
					{
						name: 'recipient',
						label: t('programPanel.post.mentibot.table.recipient'),
						body: ({ forMenteeOnly, forMentorOnly }) => {
							if (forMenteeOnly && !forMentorOnly) {
								return t('programPanel.post.mentibot.applicationTypeRecipient.onlyMentee');
							}
							if (!forMenteeOnly && forMentorOnly) {
								return t('programPanel.post.mentibot.applicationTypeRecipient.onlyMentor');
							}
							return t('programPanel.post.mentibot.applicationTypeRecipient.both');
						},
					},
				]}
			/>
		</>
	);
};

const FinalSurveyNotificationsTable = (props: {
	finalSurveyNotifications: CommunicationMentibotInfoResult['finalSurveyNotifications'];
	isFetching: boolean;
}) => {
	const { finalSurveyNotifications, isFetching } = props;
	const { currentProgram } = useScope();
	const { t } = useTranslation();

	const [selectedPreviewKey, setSelectedPreviewKey] = useState<null | string>(null);
	const previewTemplateMailContent =
		finalSurveyNotifications.find(({ key }: any) => key === selectedPreviewKey)?.template || null;

	if (currentProgram?.surveyNotifications === false) {
		return <MessageBox message={t('programPanel.post.mentibot.evaluationSurveysNotificationsDisabled')} />;
	}

	return (
		<>
			<PreviewTemplateDialog
				visible={Boolean(selectedPreviewKey) && Boolean(previewTemplateMailContent)}
				onHide={() => {
					setSelectedPreviewKey(null);
				}}
				mailTemplateContent={previewTemplateMailContent}
			/>
			<Table
				keyOptionProp="key"
				loading={isFetching}
				data={finalSurveyNotifications}
				columns={[
					{
						name: 'title',
						label: t('programPanel.post.mentibot.table.title'),
						onCellClick: ({ key }) => {
							setSelectedPreviewKey(key);
						},
					},
					{
						name: 'time',
						label: t('programPanel.post.mentibot.table.time'),
						body: ({ hours }) => prepareTimeString(t, hours),
					},
				]}
			/>
		</>
	);
};

const OtherNotificationTable = (props: {
	sessionRemindersNotifications: CommunicationMentibotInfoResult['sessionRemindersNotifications'];
	isFetching: boolean;
}) => {
	const { sessionRemindersNotifications, isFetching } = props;
	const { t } = useTranslation();

	const [selectedPreviewKey, setSelectedPreviewKey] = useState<null | string>(null);
	const previewTemplateMailContent =
		sessionRemindersNotifications.find(({ key }: any) => key === selectedPreviewKey)?.template || null;

	return (
		<>
			<PreviewTemplateDialog
				visible={Boolean(selectedPreviewKey) && Boolean(previewTemplateMailContent)}
				onHide={() => {
					setSelectedPreviewKey(null);
				}}
				mailTemplateContent={previewTemplateMailContent}
			/>
			<Table
				keyOptionProp="key"
				loading={isFetching}
				data={sessionRemindersNotifications}
				columns={[
					{
						name: 'title',
						label: t('programPanel.post.mentibot.table.title'),
						onCellClick: ({ key }) => {
							setSelectedPreviewKey(key);
						},
					},
					{
						name: 'condition',
						label: t('programPanel.post.mentibot.table.condition'),
					},
				]}
			/>
		</>
	);
};

export const ProgramPostMentibotInfo = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const { currentUser } = useAuth();
	const { data: program, panelPath } = useProgramPanel();

	const { data, isFetching } = useQuery(
		['PROGRAM_PANEL_COMMUNICATION_MENTIBOT', { programId: program?.id, lang: currentUser?.language }],
		() => programsApi.getProgramCommunicationMentibotInfo(Number(program?.id)),
		{
			enabled: true,
			initialData: { sessionNotifications: [], finalSurveyNotifications: [], sessionRemindersNotifications: [] },
		}
	);

	return (
		<>
			<Helmet title={t('programPanel.post.mentibot.title')} />
			<SubPageTitle
				title={t('programPanel.post.mentibot.title')}
				additional={program?.displayName}
				right={
					<Button
						variant="primary-text"
						label={t('actions.back')}
						onClick={() => navigate(`/${panelPath}/communication/list`)}
					/>
				}
			/>

			<div className="flex flex-column gap-4">
				<Box variant="white-bordered">
					<BoxSection>
						<div className="py-2">{t('programPanel.post.mentibot.sessionNotifications.entry')}</div>
						<SessionNotificationInfoTable
							sessionNotifications={data.sessionNotifications}
							isFetching={isFetching}
						/>
					</BoxSection>
				</Box>

				<Box variant="white-bordered">
					<BoxSection>
						<div className="py-2">
							<Trans
								t={t}
								i18nKey="programPanel.post.mentibot.feedbackNotifications.entry"
								components={[
									<Link to={{ pathname: `/${panelPath}/feedback` }} />,
									<Link to={{ pathname: `/${panelPath}/config` }} />,
								]}
							/>
						</div>
						<FinalSurveyNotificationsTable
							finalSurveyNotifications={data.finalSurveyNotifications}
							isFetching={isFetching}
						/>
					</BoxSection>
				</Box>

				<Box variant="white-bordered">
					<BoxSection>
						<div className="py-2">
							{t('programPanel.post.mentibot.sessionRemindersNotifications.entry')}
						</div>
						<OtherNotificationTable
							sessionRemindersNotifications={data.sessionRemindersNotifications}
							isFetching={isFetching}
						/>
					</BoxSection>
				</Box>
			</div>
		</>
	);
};
